import { useEffect } from "react";

import { constants, useBreakpointRange } from "@newsela/angelou";

import { setAssignmentMember } from "@/assignments/utils/assignmentActions";
import { NUMERICAL_GRADE_BANDS } from "@/constants/GradeAndLexileLevels";
import { BannerMode } from "@/types/BannerMode.interface";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { ContentLevel } from "@/types/models/ContentLevel.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import AssignAction from "@/view/AssignAction";
import { ISavingState } from "@/view/AssignActionsContainer/AssignActionsContainer.interface";
import AssignmentStatusBadge from "@/view/AssignmentStatusBadge";
import AssignmentStudentPreviewToggle from "@/view/AssignmentStudentPreviewToggle";
import contentTypes from "@/view/constants/contentTypes";
import quizOmissionReasons from "@/view/constants/quizOmissionReasons";
import uiTypes from "@/view/constants/uiTypes";
import CopyButton from "@/view/CopyButton";
import LanguageSelector from "@/view/LanguageSelector";
import LevelSelector from "@/view/LevelSelector";
import PrintSelector from "@/view/PrintSelector";
import StickyBar from "@/view/StickyBar";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import getHasSpanishLanguageLevel from "@/view/utils/getHasSpanishLanguageLevel";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import { TCurrentLevel } from "@/view/utils/useUpdateLevel";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import {
  actionsToolbarCss,
  assignAndStatusWrapperCss,
  assignmentActionsWrapperCss,
  assignPrintAndMoreWrapperCss,
  assignStatusAndStudentViewCss,
  languageAndLexileWrapperCss,
  languageLexileAndStudentWrapperCss,
  showForDesktopCss,
  showForMobileCss,
  studentViewCss,
} from "./ActionsToolbar.style";

interface ActionsToolbarInterface {
  assignModalTriggerRef: React.Ref<HTMLButtonElement> | null;
  bannerMode: BannerMode;
  content: {
    data: {
      article_levels: ArticleLevel[];
      quiz_omission_reason: keyof typeof quizOmissionReasons | null;
    };
    id: string;
    content_type: keyof typeof contentTypes;
  };
  currentAssignment: Assignment;
  currentLevel: ContentLevel;
  handlePublish: () => void;
  isLiteContent: boolean;
  isSaving: ISavingState;
  setAdvisoryModalActive: (a: boolean) => void;
  setCurrentLevel: (level: TCurrentLevel) => void;
  setIsAssignModalActive: (a: boolean) => void;
  setIsOpeningAssignmentSettings: (a: boolean) => void;
  originalAssignment: Assignment;
  userProperties: UserProperties;
}

export const ActionsToolbar = ({
  assignModalTriggerRef,
  bannerMode,
  content,
  currentAssignment,
  currentLevel,
  handlePublish,
  isLiteContent,
  isSaving,
  originalAssignment,
  setAdvisoryModalActive,
  setCurrentLevel,
  setIsAssignModalActive,
  setIsOpeningAssignmentSettings,
  userProperties,
}: ActionsToolbarInterface) => {
  const { isStudentPreviewOn } = useStudentPreviewContext();
  const { isTeacher, isStudent, isLiteUser } = userProperties;
  const { uiType } = useUserContentUIContext();
  const { isBannerModeActive } = bannerMode;
  const { dispatch, updatedAssignment } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment;
    dispatch: () => void;
  };

  useEffect(() => {
    const { assignment_members: assignmentMembers } = updatedAssignment;
    const isCurrentLevelEmpty = Object.keys(currentLevel).length === 0;

    if (
      !isCurrentLevelEmpty &&
      assignmentMembers &&
      assignmentMembers.length === 0
    ) {
      setAssignmentMember(currentLevel?.header_id, dispatch);
    }
  }, [currentLevel]);

  const {
    data: contentData,
    id: contentId,
    content_type: contentType,
  } = content;
  const levels = contentData?.article_levels || [];
  const liteUserLiteContent = uiType === uiTypes.LITE;
  const showPremiumActions = !isLiteUser || liteUserLiteContent || isStudent;
  const contentIsVideo = contentType === contentTypes.INTERACTIVE_VIDEO;

  // Find if there is a spanish version
  const hasMultipleLanguageLevels = getHasSpanishLanguageLevel(levels);
  const isLanguageHiddenForStudents =
    (isStudent || isStudentPreviewOn) &&
    getAssignmentSetting("hide_language_selector", currentAssignment);

  const showLanguageSelector =
    hasMultipleLanguageLevels &&
    !contentIsVideo &&
    !isLanguageHiddenForStudents;

  const showLanguageAndLevelSelectors = !contentIsVideo && showPremiumActions;

  const showAssignAction =
    isTeacher && showPremiumActions && !isBannerModeActive;

  const showPrintSelector =
    showPremiumActions && !isBannerModeActive && !contentIsVideo;

  const sortedLevels = [...levels].sort(
    (a, b) =>
      NUMERICAL_GRADE_BANDS[b.grade_band] - NUMERICAL_GRADE_BANDS[a.grade_band],
  );

  // Assignment settings
  const isLevelHiddenForStudents =
    (isStudent || isStudentPreviewOn) &&
    getAssignmentSetting("hide_lexile_level", currentAssignment);

  const isLevelLockedForStudents =
    (isStudent || isStudentPreviewOn) &&
    currentAssignment?.is_reading_level_locked;

  const {
    breakpoints: {
      BreakpointRanges: { xsToSm, zeroToXs, smToMd, lgToInfinity },
    },
  } = constants;
  const currentViewportRange = useBreakpointRange();
  const isMobile = currentViewportRange !== lgToInfinity;
  const useMobileAssignActions = [zeroToXs, xsToSm, smToMd].includes(
    currentViewportRange,
  );

  const renderViewAsStudentToggle = () => {
    if (isStudent) return null;

    return <AssignmentStudentPreviewToggle />;
  };

  const renderAssignAction = () => {
    if (!showAssignAction) return null;

    return (
      <AssignAction
        content={content}
        currentLevelId={currentLevel.id}
        handlePublish={handlePublish}
        isMobile={useMobileAssignActions}
        isSaving={isSaving.value}
        originalAssignment={originalAssignment}
        setAdvisoryModalActive={setAdvisoryModalActive}
        setIsOpeningAssignmentSettings={setIsOpeningAssignmentSettings}
        triggerRef={assignModalTriggerRef}
      />
    );
  };

  const renderCopyButton = () => {
    return (
      <CopyButton
        contentId={contentId}
        contentType={content.content_type}
        iconOnly={isMobile}
        setIsAssignModalActive={setIsAssignModalActive}
        triggerRef={assignModalTriggerRef}
        uiType={uiType}
      />
    );
  };

  const renderLanguageSelector = () => {
    if (!showLanguageSelector) return null;

    return (
      <LanguageSelector
        contentId={contentId}
        currentLevel={currentLevel}
        setCurrentLevel={setCurrentLevel}
        isLevelSelectorVisible={isLevelHiddenForStudents}
        sortedLevels={sortedLevels}
      />
    );
  };

  const renderLevelSelector = () => {
    if (isLevelHiddenForStudents) return null;

    return (
      <LevelSelector
        contentId={contentId}
        sortedLevels={sortedLevels}
        currentLevel={currentLevel}
        setCurrentLevel={setCurrentLevel}
        isLevelLocked={isLevelLockedForStudents}
        isLanguageSelectorVisible={showLanguageSelector}
      />
    );
  };

  const renderPrintSelector = () => {
    if (!showPrintSelector) return null;

    return (
      <PrintSelector
        contentId={contentId}
        currentLevel={currentLevel}
        isLiteContent={isLiteContent}
        isMobile={isMobile}
        quizOmissionReason={contentData?.quiz_omission_reason}
        userProperties={userProperties}
      />
    );
  };

  return (
    <StickyBar id="sticky-bar">
      <div css={actionsToolbarCss}>
        <div css={showForMobileCss}>
          <div css={assignStatusAndStudentViewCss}>
            <div css={assignAndStatusWrapperCss}>
              <AssignmentStatusBadge />
              {renderAssignAction()}
            </div>
            <div css={studentViewCss}>{renderViewAsStudentToggle()}</div>
          </div>
          <div css={assignmentActionsWrapperCss}>
            {showLanguageAndLevelSelectors && (
              <div css={languageAndLexileWrapperCss}>
                {renderLanguageSelector()}
                {renderLevelSelector()}
              </div>
            )}
            {renderCopyButton()}
            {renderPrintSelector()}
          </div>
        </div>
        <div css={showForDesktopCss}>
          <div css={languageLexileAndStudentWrapperCss}>
            {showLanguageAndLevelSelectors && (
              <div css={languageAndLexileWrapperCss}>
                {renderLanguageSelector()}
                {renderLevelSelector()}
              </div>
            )}
            <div css={studentViewCss}>{renderViewAsStudentToggle()}</div>
          </div>
          <div css={assignPrintAndMoreWrapperCss}>
            <AssignmentStatusBadge />
            {renderCopyButton()}
            {renderPrintSelector()}
            {renderAssignAction()}
          </div>
        </div>
      </div>
    </StickyBar>
  );
};

export default ActionsToolbar;
